import Vue from 'vue'

import TextToSpeech from '@/components/accessibility/TextToSpeech'

import { generateVueOptions } from '@/mains/base'

const textToSpeechVueConfig = generateVueOptions(true)

textToSpeechVueConfig.components = { TextToSpeech }

export default {
  name: 'textToSpeechMixin',
  data() {
    return { textToSpeechInstances: {} }
  },
  methods: {
    /**
     * Injects Text To Speech players on the given elements.
     *
     * Since Vue can't inject components into v-html, we have to do a little trickery by creating a new Vue instance
     * for each TTS player and manually storing and appending them.
     * @param {Element|Array<Element>|NodeList} baseElements
     * @param {string=} key
     * @param {string=} selector
     */
    injectTextToSpeech(baseElements, key = 'default', selector = 'h3, p, li') {
      this.destroyTextToSpeech(key)

      if (this.textToSpeechInstances[key] === undefined) {
        this.textToSpeechInstances[key] = []
      }

      if (baseElements.forEach === undefined) {
        baseElements = [baseElements]
      }

      // Loop over the child nodes and create the necessary TTS players
      baseElements.forEach(baseElement =>
        baseElement.querySelectorAll(selector).forEach(childNode => {
          // Don't create TTS players for empty nodes
          if (!childNode.textContent.trim()) {
            return
          }

          // Create a new Vue instance with a TTS player as it's only component
          const TextToSpeech = new Vue({
            ...textToSpeechVueConfig,
            render(h) {
              return h('TextToSpeech', { props: { text: childNode.innerHTML } })
            },
          })

          // Mount the player.
          // Mounting a Vue component directly to a node removes the contents of the node.
          // Therefore we have to render it to a separate element and use native JS to append it to the document.
          childNode.appendChild(TextToSpeech.$mount().$el)

          // Store the player instance for later reference
          this.textToSpeechInstances[key].push(TextToSpeech)
        })
      )
    },

    /**
     * Destroys all of the current TTS player instances for the given key.
     *
     * This is necessary because we've created completely new Vue instances which can't be destroyed automatically
     * by our main Vue instance.
     * @param {string=} key
     */
    destroyTextToSpeech(key = 'default') {
      if (!this.textToSpeechInstances[key]) {
        return
      }

      // Loop over all of the player instances, remove their DOM nodes and destroy the instances
      this.textToSpeechInstances[key].forEach(TextToSpeech => {
        TextToSpeech.$el.remove()
        TextToSpeech.$destroy()
      })

      // Clear the TTS player array
      delete this.textToSpeechInstances[key]
    },
  },
  beforeDestroy() {
    Object.keys(this.textToSpeechInstances).forEach(key => {
      this.destroyTextToSpeech(key)
    })
  },
}
